import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getFinanceStatistics } from "../api/apiCalls"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import dayjs from "dayjs"
import Loading from "../../../components/common/Loading"
import ValueList from "../components/Charts/ValueLists"
import ReminderPaymentCountChart from "../components/Charts/ReminderPaymentCountChart"
import PaymentStatisticsChart from "../components/Charts/PaymentStatisticsChart"
import CostCenterSalesChart from "../components/Charts/CostCenterSalesChart"
import BillTypeChart from "../components/Charts/BillTypeChart"
import FiveObjectsOrCustomerChart from "../components/Charts/FiveObjectsOrCustomerChart"
import { ReactComponent as CALENDAR } from "../../../assets/CalendarICON.svg"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { GeneralQueryKeys } from "../../../api/constants"
import RefreshStatisticsModal from "../components/RefreshModal/RefreshStatisticsModal"
import { useRefreshStatisticsMutation } from "../api/services"
import { formatCurrency } from "../../../services/functions"

export default function Dashboard() {
    // STATISTIC FOR NOW
    const [startDate, setstartDate] = useState<string>(dayjs().startOf("year").format("YYYY-MM-DD"))
    const [endDate, setendDate] = useState<string>(dayjs().format("YYYY-MM-DD"))
    const queryKey = GeneralQueryKeys.FINANCE_STATISTICS + startDate + endDate
    const { data: paymentStatistics, isLoading: paymentStatisticsLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getFinanceStatistics({
                monthStart: dayjs(startDate).toDate(),
                monthEnd: dayjs(endDate).toDate(),
            }),
    })

    const key = `payreq_requested_from_to_${startDate}_${endDate}`
    const paymentStatsForRange = (paymentStatistics?.[key] ?? []) as any[]
    const totalIncome = paymentStatsForRange.reduce((sum, item) => sum + Number(item?.received_amount ?? 0), 0)
    const totalRequestedIncome = paymentStatsForRange.reduce((sum, item) => sum + Number(item?.requested_amount ?? 0), 0)
    const totalOutstanding = totalRequestedIncome - totalIncome
    const queryClient = useQueryClient()
    const { mutate: refreshStatistics } = useRefreshStatisticsMutation(queryClient, queryKey)
    // FRONTEND LOGIC
    const handleRefresh = (start: string, end: string) => {
        refreshStatistics({
            monthStart: dayjs(start).toDate(),
            monthEnd: dayjs(end).toDate(),
        })
    }
    const modalCon = useContext(ModalContext)

    return (
        <BasePage>
            <HeadBanner
                title="Dashboard"
                button={
                    <button className="btn btn-primary btn-sm sm:btn-md px-3 sm:px-4 gap-2 flex-nowrap whitespace-nowrap text-xs sm:text-sm" onClick={() => modalCon?.trigger(0)}>
                        <CALENDAR className="w-4 h-4 sm:w-6 sm:h-6 shrink-0" />
                        {dayjs(startDate).format("DD.MM.YY")}–{dayjs(endDate).format("DD.MM.YY")}
                    </button>
                }
            />
            {paymentStatisticsLoading && <Loading />}
            {!paymentStatisticsLoading && paymentStatistics && (
                <BaseGrowBody classProps="overflow-y-scroll overflow-x-hidden h-full ">
                    <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                        <div className="bg-white rounded-default shadow-lg p-4">
                            <p className="text-xs text-gray-500">Einnahmen (erhalten, gesamt)</p>
                            <p className="text-2xl font-semibold text-green-500">{formatCurrency(totalIncome)}</p>
                            <p className="text-xs text-gray-400">
                                {dayjs(startDate).format("DD.MM.YYYY")} – {dayjs(endDate).format("DD.MM.YYYY")}
                            </p>
                        </div>
                        <div className="bg-white rounded-default shadow-lg p-4">
                            <p className="text-xs text-gray-500">In Rechnung gestellt (gesamt)</p>
                            <p className="text-2xl font-semibold">{formatCurrency(totalRequestedIncome)}</p>
                            <p className="text-xs text-gray-400">
                                {dayjs(startDate).format("DD.MM.YYYY")} – {dayjs(endDate).format("DD.MM.YYYY")}
                            </p>
                        </div>
                        <div className="bg-white rounded-default shadow-lg p-4">
                            <p className="text-xs text-gray-500">Außenstände</p>
                            <p className="text-2xl font-semibold text-red-500">{formatCurrency(totalOutstanding)}</p>
                            <p className="text-xs text-gray-400">
                                {dayjs(startDate).format("DD.MM.YYYY")} – {dayjs(endDate).format("DD.MM.YYYY")}
                            </p>
                        </div>
                    </div>
                    <div className="w-full flex flex-col md:flex-row gap-4 py-4 min-h-fit">
                        {paymentStatistics && <PaymentStatisticsChart paymentStatistics={paymentStatistics[key]} />}
                        <div className="w-full md:w-1/3 flex flex-col gap-4 h-fit">
                            {paymentStatistics && <ReminderPaymentCountChart paymentStatistics={paymentStatistics[key]} />}
                            {paymentStatistics && <BillTypeChart paymentStatistics={paymentStatistics[key]} />}
                        </div>
                    </div>
                    <div className="w-full flex flex-row flex-wrap gap-4 pb-4 min-h-0">
                        {paymentStatistics && <CostCenterSalesChart paymentStatistics={paymentStatistics.cost_center} />}
                        {paymentStatistics && <FiveObjectsOrCustomerChart statistics={paymentStatistics.customer_highest_sales_volume} collection="customer" type="high" />}
                        {paymentStatistics && <FiveObjectsOrCustomerChart statistics={paymentStatistics.customer_lowest_sales_volume.reverse()} collection="customer" type="low" />}
                        <ValueList label={"Verhältnisse (365 Tage)"} ul={paymentStatistics.reminder_rate_last_year} />
                        {paymentStatistics && <FiveObjectsOrCustomerChart statistics={paymentStatistics.objects_highest_sales_volume} collection="objects" type="high" />}
                        {paymentStatistics && <FiveObjectsOrCustomerChart statistics={paymentStatistics.objects_lowest_sales_volume.reverse()} collection="objects" type="low" />}
                    </div>
                </BaseGrowBody>
            )}
            <RefreshStatisticsModal
                modalClose={() => modalCon?.trigger(0)}
                setStartDate={setstartDate}
                setEndDate={setendDate}
                endDate={endDate}
                startDate={startDate}
                modalId={0}
                handleRefresh={handleRefresh}
            />
        </BasePage>
    )
}
